// SPDX-License-Identifier: AGPL-3.0-or-later
import { ExtensionUrl, fieldTypeToItemType } from '@intakeform/fhir';
/** Generates a short random suffix for draft `linkId`s. Crypto-strong if available. */
export function newLinkId(prefix) {
    const cryptoApi = typeof globalThis.crypto !== 'undefined' ? globalThis.crypto : undefined;
    if (cryptoApi !== undefined && typeof cryptoApi.randomUUID === 'function') {
        return `${prefix}-${cryptoApi.randomUUID().slice(0, 8)}`;
    }
    return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}
function base(type, text) {
    return {
        linkId: newLinkId(type),
        type: fieldTypeToItemType(type),
        text,
    };
}
/** The nine built-in palette entries, in the order shown to clinicians. */
export const defaultPalette = [
    {
        id: 'shortText',
        label: 'Text',
        description: 'Single-line text input. Set a Format (email, phone, address) in the field config to enable extra validation.',
        category: 'text',
        template: () => base('shortText', 'Text question'),
    },
    {
        id: 'longText',
        label: 'Long text',
        description: 'Multi-line free text input.',
        category: 'text',
        template: () => base('longText', 'Long text question'),
    },
    {
        id: 'singleSelect',
        label: 'Single-select',
        description: 'Choose one option from a list.',
        category: 'choice',
        template: () => ({
            ...base('singleSelect', 'Pick one'),
            answerOption: [{ valueString: 'Option 1' }, { valueString: 'Option 2' }],
        }),
    },
    {
        id: 'multiSelect',
        label: 'Multi-select',
        description: 'Pick any number of options.',
        category: 'choice',
        template: () => ({
            ...base('multiSelect', 'Pick any that apply'),
            repeats: true,
            answerOption: [{ valueString: 'Option 1' }, { valueString: 'Option 2' }],
        }),
    },
    {
        id: 'number',
        label: 'Number',
        description: 'Numeric input with optional min/max.',
        category: 'numeric',
        template: () => base('number', 'Number question'),
    },
    {
        id: 'date',
        label: 'Date',
        description: 'Calendar date (YYYY-MM-DD).',
        category: 'temporal',
        template: () => base('date', 'Date question'),
    },
    {
        id: 'yesNo',
        label: 'Yes / No',
        description: 'Boolean question.',
        category: 'consent',
        template: () => base('yesNo', 'Yes/no question'),
    },
    {
        id: 'signature',
        label: 'Signature',
        description: 'Canvas signature capture, stored as a PNG attachment.',
        category: 'consent',
        template: () => ({
            ...base('signature', 'Sign to consent'),
            required: true,
            extension: [{ url: ExtensionUrl.signature, valueBoolean: true }],
        }),
    },
    {
        id: 'fileUpload',
        label: 'File upload',
        description: 'Upload a single file (e.g. insurance card).',
        category: 'upload',
        template: () => base('fileUpload', 'File upload question'),
    },
    {
        id: 'insuranceCard',
        label: 'Insurance card',
        description: 'Photo of the insurance card. After upload, OCR fills the payor / member / group items below.',
        category: 'upload',
        template: () => ({
            ...base('fileUpload', 'Upload your insurance card'),
            // Marker the patient renderer uses to trigger the OCR pass
            // once the upload completes (bead 2pc). The renderer then
            // applies parsed fields to items directly below this one whose
            // `insurance-card-target` extension names the matching field.
            extension: [{ url: ExtensionUrl.insuranceCard, valueBoolean: true }],
        }),
    },
];
export const sectionTemplates = [
    {
        id: 'contact',
        label: 'Contact Information',
        description: 'Patient name, email, phone number, and birth date.',
        template: () => ({
            linkId: newLinkId('section-contact'),
            type: 'group',
            text: 'Contact Information',
            item: [
                { ...base('shortText', 'Full name'), required: true },
                { ...base('shortText', 'Email address'), required: true },
                { ...base('shortText', 'Phone number') },
                { ...base('date', 'Date of birth'), required: true },
            ],
        }),
    },
    {
        id: 'medicalHistory',
        label: 'Medical History',
        description: 'Primary medical conditions, surgeries, and family history.',
        template: () => ({
            linkId: newLinkId('section-history'),
            type: 'group',
            text: 'Medical History',
            item: [
                {
                    ...base('multiSelect', 'Primary medical conditions'),
                    repeats: true,
                    answerOption: [
                        { valueString: 'Hypertension' },
                        { valueString: 'Diabetes' },
                        { valueString: 'Asthma' },
                        { valueString: 'Heart disease' },
                        { valueString: 'Other' },
                    ],
                },
                { ...base('longText', 'Prior surgeries & hospitalizations') },
                { ...base('longText', 'Family medical history') },
            ],
        }),
    },
    {
        id: 'allergies',
        label: 'Allergies',
        description: 'Known allergies to medications, food, and other substances.',
        template: () => ({
            linkId: newLinkId('section-allergies'),
            type: 'group',
            text: 'Allergies',
            item: [
                {
                    ...base('multiSelect', 'Drug allergies'),
                    repeats: true,
                    answerOption: [
                        { valueString: 'Penicillin' },
                        { valueString: 'Sulfa drugs' },
                        { valueString: 'Aspirin/NSAIDs' },
                        { valueString: 'None' },
                        { valueString: 'Other' },
                    ],
                },
                {
                    ...base('multiSelect', 'Food allergies'),
                    repeats: true,
                    answerOption: [
                        { valueString: 'Nuts' },
                        { valueString: 'Dairy' },
                        { valueString: 'Shellfish' },
                        { valueString: 'None' },
                        { valueString: 'Other' },
                    ],
                },
                { ...base('longText', 'Other allergies (environmental, latex, etc.)') },
            ],
        }),
    },
    {
        id: 'insurance',
        label: 'Insurance Details',
        description: 'Insurance card with OCR auto-fill for provider, policy, and group.',
        // Card first so the OCR pass (insurance-card-target items below it
        // in document order) can populate the three text fields. Same widget
        // as the standalone 'Insurance card' palette entry — picking this
        // section gives the patient the OCR-enabled flow, not the dumb upload.
        template: () => ({
            linkId: newLinkId('section-insurance'),
            type: 'group',
            text: 'Insurance Details',
            item: [
                {
                    ...base('fileUpload', 'Upload your insurance card'),
                    required: true,
                    extension: [{ url: ExtensionUrl.insuranceCard, valueBoolean: true }],
                },
                {
                    ...base('shortText', 'Provider name'),
                    required: true,
                    extension: [{ url: ExtensionUrl.insuranceCardTarget, valueCode: 'payor' }],
                },
                {
                    ...base('shortText', 'Member / Policy number'),
                    required: true,
                    extension: [{ url: ExtensionUrl.insuranceCardTarget, valueCode: 'memberId' }],
                },
                {
                    ...base('shortText', 'Group number'),
                    extension: [{ url: ExtensionUrl.insuranceCardTarget, valueCode: 'groupNumber' }],
                },
            ],
        }),
    },
    {
        id: 'medications',
        label: 'Medications',
        description: 'Current medications list with dosage and frequency.',
        template: () => ({
            linkId: newLinkId('section-medications'),
            type: 'group',
            text: 'Medications',
            item: [
                {
                    ...base('yesNo', 'Are you currently taking any prescription or over-the-counter medications?'),
                    required: true,
                },
                { ...base('longText', 'Please list all current medications (Name, Dosage, Frequency)') },
                { ...base('shortText', 'Preferred pharmacy name & address') },
            ],
        }),
    },
    {
        id: 'consent',
        label: 'Consent & Signature',
        description: 'Required clinic consent agreement and digital signature.',
        template: () => ({
            linkId: newLinkId('section-consent'),
            type: 'group',
            text: 'Consent & Signature',
            item: [
                {
                    ...base('yesNo', 'I consent to receive healthcare services and treatment as described.'),
                    required: true,
                },
                {
                    ...base('signature', 'Patient signature'),
                    required: true,
                    extension: [{ url: ExtensionUrl.signature, valueBoolean: true }],
                },
            ],
        }),
    },
];
//# sourceMappingURL=palette-entries.js.map